import React, { useState, useEffect, useMemo } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent, IconButton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Download,
  PictureAsPdf,
  Description,
  DateRange, Close as CloseIcon,
} from '@mui/icons-material';
import { formatPositionsDisplay } from '../services/positionAPIs';
import ExportButtons from '../components/btns/ExportButtons';
import enGB from 'date-fns/locale/en-GB'; // en-GB uses dd/MM/yyyy
import { Calendar, DollarSign, Activity, Heart, Clock } from 'lucide-react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { exportAttendanceToExcel, exportAttendanceToWord, exportAttendanceTableToExcel } from '../utils/reportExportUtils';
//Components
import PageHeader from '../components/headers/Header';
import AbsenceAccordion from '../components/accordions/AbsenceAccordion';
import EmployeeDataTable from '../components/tables/Attendance';
import EmployeeAttendanceReportDialog from '../components/DViews/EmployeeAttendance';
import HighlightCalendar from '../components/calendars/HighlightCalendar.jsx';
// Helpers
import { formatDate, formatHoursAsDays, formatDateForDisplay, groupConsecutiveDates } from './helper';


// API Services
import {
  getAttendanceReport, getAttendanceSummary, getEmployeesWithHighAbsence,
  getYearlyAttendanceSummary, getEmployeeAttendanceReport
} from '../services/reportingAPIs';

const AttendanceReportTable = () => {
  const theme = useTheme();
  const componentRef = useRef();
  const [open, setOpen] = useState(false);
  const [highlightedDates, setHighlightedDates] = useState([]);
  const [data, setData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [yearSummary, setYearSummary] = useState([]);
  const [highAbsences, setHighAbsences] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null
  });
  const [nameFilter, setNameFilter] = useState('');
  const formatDateForBackend = (date) => {
    if (!date) return null;
    const d = new Date(date);
    // Force the date to be interpreted as local midnight, then extract YYYY-MM-DD
    return d.toLocaleDateString('en-CA'); // 'en-CA' gives YYYY-MM-DD format
  };
  const filteredMainData = useMemo(() => {
    if (!nameFilter.trim()) return data;
    const searchTerm = nameFilter.toLowerCase().trim();
    return data.filter(emp =>
      emp.employeeName?.toLowerCase().includes(searchTerm)
    );
  }, [data, nameFilter]);

  //// Exporting
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: 'Attendance Report',
    // Pdf Padding
    pageStyle: `
    @page { size: landscape; }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .attendanceReportContainer {
          padding:3,
        }
      }
    `
  });

  const handleExportExcel = () => {
    exportAttendanceToExcel(data, dateRange);
  };

  const handleExportWord = () => {
    exportAttendanceToWord(data, dateRange);
  };

  const handleExportTableExcel = () => {
    exportAttendanceTableToExcel(filteredMainData, dateRange);
  };

  // Fetch raw data from backend (only filtered by date)
  useEffect(() => {
    fetchData();
  }, [dateRange.from, dateRange.to]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Helper to format date as YYYY-MM-DD without timezone shift
      const formatDateForBackend = (date) => {
        if (!date) return null;
        return new Date(date).toLocaleDateString('en-CA'); // e.g., "2024-06-15"
      };
      const from = formatDateForBackend(dateRange.from);
      const to = formatDateForBackend(dateRange.to);

      const result = await getAttendanceReport(from, to);
      console.log('Result:', result)
      const summary = await getAttendanceSummary(from, to);
      console.log('Summery:', summary)
      const highAbsenceData = await getEmployeesWithHighAbsence(from, to);
      const yearData = await getYearlyAttendanceSummary();
      setSummaryData(summary || {});
      setData(result || []);
      setHighAbsences(highAbsenceData || []);
      setYearSummary(yearData);
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Client-side filter by activeFilter (since backend doesn't support it)
  const filteredData = useMemo(() => {
    if (activeFilter === 'ALL') {
      return data; // Show all employees, even if they have zero in all types
    }
    return data.filter(employee => {
      if (['ANNUAL_LEAVE', 'UNPAID_LEAVE'].includes(activeFilter)) {
        return employee.leaveDays[activeFilter] > 0;
      } else if (activeFilter === 'SICKNESS') {
        return employee.totalSicknessRecords > 0;
      } else if (activeFilter === 'LATENESS') {
        return employee.totalLatenessRecords > 0;
      } else if (activeFilter === 'PERSONAL') {
        return employee.totalPersonalRecords > 0;
      }
      return false;
    });
  }, [data, activeFilter]);


  const getFilterLabel = (filter) => {
    const labels = {
      ALL: 'All Types',
      ANNUAL_LEAVE: 'Annual Leave',
      UNPAID_LEAVE: 'Unpaid Leave',
      SICKNESS: 'Sickness',
      LATENESS: 'Lateness',
      PERSONAL: 'Personal'
    };
    return labels[filter] || filter;
  };

  const filterButtons = [
    { id: 'ALL', label: 'All', color: 'info' },
    { id: 'ANNUAL_LEAVE', label: 'Annual Leave', color: 'primary' },
    { id: 'UNPAID_LEAVE', label: 'Unpaid Leave', color: 'default' },
    { id: 'SICKNESS', label: 'Sickness', color: 'error' },
    { id: 'LATENESS', label: 'Lateness', color: 'warning' },
    { id: 'PERSONAL', label: 'Personal', color: 'secondary' }
  ];

  const formatLatenessHours = (hours) => {
    if (!hours || hours <= 0) return '0 min';
    if (hours >= 1) return `${hours.toFixed(1)} hrs`;
    return `${Math.round(hours * 60)} min`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB} >
      <Box ref={componentRef} className='attendanceReportContainer' sx={{ padding: 1 }}>
        {/* <Typography variant="h4" gutterBottom sx={{ color: '#2196F3', fontWeight: 'bold' }}>
          <DateRange sx={{ mr: 1, verticalAlign: 'middle' }} />
          Attendance Report
        </Typography> */}
        <PageHeader title="Attendance Report" subtitle="View and track employee attendance records" />
        <EmployeeDataTable data={yearSummary} />

        {/* Filters & Export Card — matches LeaveReport UI */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" mb={2} gutterBottom>
              Report Filters & Export Options
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.from}
                  onChange={(date) => setDateRange(prev => ({ ...prev, from: date }))}
                  inputFormat="dd/MM/yyyy"
                  disableMaskedInput
                  slotProps={{
                    textField: {
                      size: 'small',
                      readOnly: true, // Disable direct input
                      sx: { bgcolor: 'background.paper', borderRadius: 1 }
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="End Date"
                  value={dateRange.to}
                  onChange={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                  slotProps={{
                    textField: {
                      size: 'small',
                      readOnly: true, // Disable direct input
                      sx: { bgcolor: 'background.paper', borderRadius: 1 }
                    },
                  }}
                  inputFormat="dd/MM/yyyy"
                //readOnly
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="outlined"
                  onClick={() => setDateRange({ from: null, to: null })}
                  fullWidth
                  size="small"
                  sx={{ height: '40px' }}
                >
                  Clear Dates
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <ExportButtons
              onExportPDF={handlePrint}
              onExportExcel={handleExportExcel}
              onExportWord={handleExportWord}
              disabled={filteredData.length === 0}
            />
          </CardContent>
        </Card>
        {/* ✅ SUMMARY CARDS — Simple & Clear */}
        <Grid container sx={{ width: '100%' }} spacing={3}>
          {/* Annual Leave */}
          <Grid item sx={{ flex: 1 }}>
            <Card
              onClick={() => setActiveFilter('ANNUAL_LEAVE')}
              sx={{
                cursor: 'pointer',
                height: '100%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(102, 126, 234, 0.3)'
                }
              }}
            >
              <CardContent sx={{ position: 'relative', pb: 2 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2
                }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Annual Leave
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {summaryData.annualLeaveDays || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      days
                    </Typography>
                  </Box>
                  <Box sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Calendar size={24} />
                  </Box>
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Clock size={16} />
                  <Typography variant="body2" fontWeight="medium">
                    {formatLatenessHours(summaryData.annualLeaveHours)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Unpaid Leave */}
          <Grid item xs sx={{ flex: 1 }}>
            <Card
              onClick={() => setActiveFilter('UNPAID_LEAVE')}
              sx={{
                cursor: 'pointer',
                height: '100%',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(245, 87, 108, 0.3)'
                }
              }}
            >
              <CardContent sx={{ position: 'relative', pb: 2 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2
                }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Unpaid Leave
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {summaryData.unpaidLeaveDays || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      days
                    </Typography>
                  </Box>
                  <Box sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <DollarSign size={24} />
                  </Box>
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Clock size={16} />
                  <Typography variant="body2" fontWeight="medium">
                    {formatLatenessHours(summaryData.unpaidLeaveHours)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Sickness */}
          <Grid item xs sx={{ flex: 1 }}>
            <Card
              onClick={() => setActiveFilter('SICKNESS')}
              sx={{
                cursor: 'pointer',
                height: '100%',
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(250, 112, 154, 0.3)'
                }
              }}
            >
              <CardContent sx={{ position: 'relative', pb: 2 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2
                }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Sickness
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {summaryData.sicknessRecords || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      records
                    </Typography>
                  </Box>
                  <Box sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Activity size={24} />
                  </Box>
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Clock size={16} />
                  <Typography variant="body2" fontWeight="medium">
                    {formatLatenessHours(summaryData.sicknessHours)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Personal Commitment */}
          <Grid item xs sx={{ flex: 1 }}>
            <Card
              onClick={() => setActiveFilter('PERSONAL')}
              sx={{
                cursor: 'pointer',
                height: '100%',
                background: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
                color: 'white',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(161, 140, 209, 0.3)'
                }
              }}
            >
              <CardContent sx={{ position: 'relative', pb: 2 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2
                }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Personal Commitment
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {summaryData.personalRecords || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      records
                    </Typography>
                  </Box>
                  <Box sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Heart size={24} />
                  </Box>
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Clock size={16} />
                  <Typography variant="body2" fontWeight="medium">
                    {formatLatenessHours(summaryData.personalHours)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Lateness */}
          <Grid item xs sx={{ flex: 1 }}>
            <Card
              onClick={() => setActiveFilter('LATENESS')}
              sx={{
                cursor: 'pointer',
                height: '100%',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(79, 172, 254, 0.3)'
                }
              }}
            >
              <CardContent sx={{ position: 'relative', pb: 2 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2
                }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Lateness
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {summaryData.latenessRecords || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      records
                    </Typography>
                  </Box>
                  <Box sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Clock size={24} />
                  </Box>
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Clock size={16} />
                  <Typography variant="body2" fontWeight="medium">
                    {formatLatenessHours(summaryData.latenessHours)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <AbsenceAccordion highAbsences={highAbsences} number={5} sickN={3} />

        {/* Filter Tabs */}
        {/* <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {filterButtons.map((button) => (
            <Button
              key={button.id}
              variant={activeFilter === button.id ? 'contained' : 'outlined'}
              color={button.color}
              onClick={() => setActiveFilter(button.id)}
              sx={{
                textTransform: 'none',
                fontWeight: activeFilter === button.id ? 600 : 400
              }}
            >
              {button.label}
            </Button>
          ))}
        </Box> */}
        <Dialog open={activeFilter !== 'ALL'} onClose={() => activeFilter === 'ALL'} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" component="h2" fontWeight="600">
                {getFilterLabel(activeFilter)} Records ({filteredData.length} employees)
              </Typography>
              <IconButton onClick={() => setActiveFilter('ALL')} disabled={loading} sx={{ color: 'grey.500' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {activeFilter !== 'ALL' && // 👇 Existing single-type table
              <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee Name</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Position</TableCell>
                      <TableCell align="center">Days</TableCell>
                      {!(['ANNUAL_LEAVE', 'UNPAID_LEAVE'].includes(activeFilter)) && <TableCell align="center">Records</TableCell>}
                      <TableCell align="center">Hours</TableCell>
                      <TableCell>Dates</TableCell>
                      {/* <TableCell align="center">Balance</TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="textSecondary">
                            No {getFilterLabel(activeFilter).toLowerCase()} records found for this period.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredData.map((employee) => {
                        let days, records, hours, dates, balance;
                        if (['ANNUAL_LEAVE', 'UNPAID_LEAVE'].includes(activeFilter)) {
                          days = employee.leaveDays[activeFilter] || 0;
                          hours = employee.leaveHours[activeFilter] || 0;
                          dates = employee.leaveDates[activeFilter] || [];
                          balance = employee.leaveBalances[activeFilter] || 0;
                        } else if (activeFilter === 'SICKNESS') {
                          days = formatHoursAsDays(employee.totalSicknessHours) || 0;
                          records = employee.totalSicknessRecords || 0;
                          hours = employee.totalSicknessHours || 0;
                          dates = employee.sicknessDates || [];
                          balance = 'unlimited';
                        } else if (activeFilter === 'LATENESS') {
                          days = formatHoursAsDays(employee.totalLatenessHours) || 0;
                          records = employee.totalLatenessRecords || 0;
                          hours = employee.totalLatenessHours || 0;
                          dates = employee.absenceDates['LATENESS'] || [];
                          balance = 'unlimited';
                        } else if (activeFilter === 'PERSONAL') {
                          days = formatHoursAsDays(employee.totalPersonalHours) || 0;
                          records = employee.totalPersonalRecords || 0;
                          hours = employee.totalPersonalHours || 0;
                          dates = employee.absenceDates['PERSONAL'] || [];
                          balance = 'unlimited';
                        }

                        return (
                          <TableRow key={employee.employeeId} hover>
                            <TableCell>{employee.employeeName}</TableCell>
                            <TableCell>{employee.department}</TableCell>
                            <TableCell>{employee.position}</TableCell>
                            <TableCell align="center">{days}</TableCell>
                            {!(['ANNUAL_LEAVE', 'UNPAID_LEAVE'].includes(activeFilter)) && <TableCell align="center">{records || '-'}</TableCell>}
                            <TableCell align="center">{formatLatenessHours(hours)}</TableCell>
                            <TableCell>
                              <Box
                                onClick={() => {
                                  setOpen(true);
                                  const dateObjects = dates.map(str => new Date(str));
                                  setHighlightedDates(dateObjects);
                                  //setHighlightedDates(dates);
                                  console.log('Highlighted Dates set to:', dates);
                                  console.log('highlightedDates:', highlightedDates);
                                }}
                                sx={{
                                  color: '#00796B',
                                  cursor: 'pointer',
                                  bgcolor: '#B3EBF2',
                                  borderRadius: 1,
                                  textAlign: 'center',
                                  '&:hover': { bgcolor: '#92e6f1ff' }
                                }}
                              >View Calendar</Box>
                              {/* {dates.length > 0 
                    ? groupConsecutiveDates(dates).join(', ')
                    : '-'} */}
                            </TableCell>
                            {/* <TableCell align="center">{balance}</TableCell> */}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            }  </DialogContent>
        </Dialog>
        {/* Table */}
        <Card>
          <CardContent>
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              // justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mb: 2
            }}>
              <Typography variant="h6" fontWeight="600">
                All Records ({filteredMainData.length} employee{filteredMainData.length !== 1 ? 's' : ''})
              </Typography>
              <TextField
                label="Filter by name"
                variant="outlined"
                size="small"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                sx={{ width: { xs: '100%', sm: 300 }, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', borderRadius: 1 } }}
                placeholder="Search employee name..."
                InputProps={{
                  endAdornment: nameFilter && (
                    <IconButton
                      size="small"
                      onClick={() => setNameFilter('')}
                      edge="end"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleExportTableExcel}
                disabled={filteredMainData.length === 0}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  height: 40,
                  bgcolor: '#388e3c',
                  '&:hover': { bgcolor: '#1b5e20' },
                }}
              >
                Export Excel
              </Button>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                      Employee Name
                    </TableCell>

                    {/* Annual Leave */}
                    <TableCell colSpan={4} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#bbdefb', position: 'sticky', top: 0, zIndex: 3, }}>
                      Annual Leave
                    </TableCell>

                    {/* Sickness */}
                    <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#ffe0b2', position: 'sticky', top: 0, zIndex: 3, }}>
                      Sickness
                    </TableCell>

                    {/* Lateness */}
                    <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e1bee7', position: 'sticky', top: 0, zIndex: 3, }}>
                      Lateness
                    </TableCell>

                    {/* Personal */}
                    <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#c8e6c9', position: 'sticky', top: 0, zIndex: 3, }}>
                      Personal
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    {/* Annual Leave */}
                    <TableCell align="center" sx={{ backgroundColor: '#e3f2fd', position: 'sticky', top: 36, zIndex: 3, }}>Annual vacation</TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#e3f2fd', position: 'sticky', top: 36, zIndex: 3, }}>Vacation Taken</TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#e3f2fd', position: 'sticky', top: 36, zIndex: 3, }}>Bal</TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#e3f2fd', position: 'sticky', top: 36, zIndex: 3, }}>Unpaid vacation</TableCell>

                    {/* Sickness */}
                    <TableCell align="center" sx={{ backgroundColor: 'var(--warning-light, #fff3e0)', position: 'sticky', top: 36, zIndex: 3 }}>Rec</TableCell>
                    <TableCell align="center" sx={{ backgroundColor: 'var(--warning-light, #fff3e0)', position: 'sticky', top: 36, zIndex: 3, }}>Days</TableCell>

                    {/* Lateness */}
                    <TableCell align="center" sx={{ backgroundColor: '#f3e5f5', position: 'sticky', top: 36, zIndex: 3, }}>Rec</TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#f3e5f5', position: 'sticky', top: 36, zIndex: 3, }}>Hrs</TableCell>

                    {/* Personal */}
                    <TableCell align="center" sx={{ backgroundColor: '#e8f5e9', position: 'sticky', top: 36, zIndex: 3, }}>Rec</TableCell>
                    <TableCell align="center" sx={{ backgroundColor: '#e8f5e9', position: 'sticky', top: 36, zIndex: 3, }}>Hrs</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredMainData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          {nameFilter.trim()
                            ? `No employees found matching "${nameFilter}"`
                            : 'No employee data available'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMainData.map((employee) => (
                      <TableRow
                        key={employee.employeeId}
                        hover
                        onClick={() => setSelectedEmployee(employee.employeeId)}
                      >
                        <TableCell>{employee.employeeName || 'N/A'}</TableCell>

                        {/* Annual Leave */}
                        <TableCell align="center" sx={{ backgroundColor: '#f7fbff' }}>
                          {employee.annualVacation || ((employee.leaveDays?.ANNUAL_LEAVE || 0) + (employee.leaveBalances?.ANNUAL_LEAVE || 0))}
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#f7fbff' }}>
                          {employee.leaveDays?.ANNUAL_LEAVE || 0}
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#f7fbff' }}>
                          {employee.leaveBalances?.ANNUAL_LEAVE || 0}
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#f7fbff' }}>
                          {employee.leaveDays?.UNPAID_LEAVE || 0}
                        </TableCell>

                        {/* Sickness */}
                        <TableCell align="center" sx={{ backgroundColor: 'var(--warning-lighter, #fffaf3)' }}>
                          {employee.totalSicknessRecords || 0}
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: 'var(--warning-lighter, #fffaf3)' }}>
                          {Number(employee.totalSicknessHours / 8 || 0).toFixed(1)}
                        </TableCell>

                        {/* Lateness */}
                        <TableCell align="center" sx={{ backgroundColor: '#faf5fb' }}>
                          {employee.totalLatenessRecords || 0}
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#faf5fb' }}>
                          {Number(employee.totalLatenessHours || 0).toFixed(1)}
                        </TableCell>

                        {/* Personal */}
                        <TableCell align="center" sx={{ backgroundColor: '#f5fbf6' }}>
                          {employee.totalPersonalRecords || 0}
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#f5fbf6' }}>
                          {Number(employee.totalPersonalHours || 0).toFixed(1)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
        {/* //) : ( */}


        <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
          Report Period: {formatDateForDisplay(data[0]?.reportStartDate)} to {formatDateForDisplay(data[0]?.reportEndDate)}
        </Typography>
      </Box>
      <EmployeeAttendanceReportDialog
        open={!!selectedEmployee}
        onClose={() => setSelectedEmployee('')}
        employeeId={selectedEmployee}
        endDate={dateRange.to ? new Date(dateRange.to).toISOString().split('T')[0] : null}
      />
      <HighlightCalendar highlightedDates={highlightedDates} open={open} setOpen={setOpen} />
    </LocalizationProvider>
  );
};

export default AttendanceReportTable;

// import React, { useState, useEffect } from 'react';
// import {
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Paper,
//   Button,
//   CircularProgress,
//   Alert,
//   Box,
//   Typography,
//   ButtonGroup
// } from '@mui/material';
// import { getAttendanceReport } from '../services/reportingAPIs';

// const AttendanceReportTable = () => {
//   const [data, setData] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [activeFilter, setActiveFilter] = useState('ANNUAL_LEAVE');
//   const [dateRange, setDateRange] = useState({ from: null, to: null });

//   useEffect(() => {
//     fetchData();
//   }, [dateRange]);

//   const fetchData = async () => {
//     try {
//       setLoading(true);
//       setError(null);
//       const result = await getAttendanceReport(dateRange.from, dateRange.to);
//       setData(result);
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const getFilteredData = () => {
//     return data.filter(employee => {
//       if (['ANNUAL_LEAVE', 'UNPAID_LEAVE'].includes(activeFilter)) {
//         return employee.leaveDays[activeFilter] > 0;
//       } else if (activeFilter === 'SICKNESS') {
//         return employee.leaveDays['SICK_LEAVE'] > 0;
//       } else if (activeFilter === 'LATENESS') {
//         return employee.totalLatenessRecords > 0;
//       } else if (activeFilter === 'PERSONAL') {
//         return employee.totalPersonalRecords > 0;
//       }
//       return false;
//     });
//   };

//   const renderTableContent = () => {
//     const filteredData = getFilteredData();

//     if (filteredData.length === 0) {
//       return (
//         <TableRow>
//           <TableCell colSpan={7} align="center" style={{ padding: '40px' }}>
//             <Typography color="textSecondary">
//               No {activeFilter.replace('_', ' ').toLowerCase()} records found for this period
//             </Typography>
//           </TableCell>
//         </TableRow>
//       );
//     }

//     return filteredData.map((employee) => {
//       let days, hours, dates, balance;

//       if (['ANNUAL_LEAVE', 'UNPAID_LEAVE'].includes(activeFilter)) {
//         days = employee.leaveDays[activeFilter] || 0;
//         hours = employee.leaveHours[activeFilter] || 0;
//         dates = employee.leaveDates[activeFilter] || [];
//         balance = employee.leaveBalances[activeFilter] || 0;
//       } else if (activeFilter === 'SICKNESS') {
//         days = employee.leaveDays['SICK_LEAVE'] || 0;
//         hours = employee.leaveHours['SICK_LEAVE'] || 0;
//         dates = employee.leaveDates['SICK_LEAVE'] || [];
//         balance = '-';
//       } else if (activeFilter === 'LATENESS') {
//         days = employee.totalLatenessRecords;
//         hours = employee.totalLatenessHours;
//         dates = employee.absenceDates['LATENESS'] || [];
//         balance = '-';
//       } else if (activeFilter === 'PERSONAL') {
//         days = employee.totalPersonalRecords;
//         hours = employee.totalPersonalHours;
//         dates = employee.absenceDates['PERSONAL'] || [];
//         balance = '-';
//       }

//       return (
//         <TableRow key={employee.employeeId} hover>
//           <TableCell>{employee.employeeName}</TableCell>
//           <TableCell>{employee.department}</TableCell>
//           <TableCell>{employee.position}</TableCell>
//           <TableCell align="center">{days}</TableCell>
//           <TableCell align="center">{hours.toFixed(2)}</TableCell>
//           <TableCell>{dates.length > 0 ? dates.join(', ') : '-'}</TableCell>
//           <TableCell align="center">{balance}</TableCell>
//         </TableRow>
//       );
//     });
//   };

//   const filterButtons = [
//     { id: 'ANNUAL_LEAVE', label: 'Annual Leave', color: 'primary' },
//     { id: 'UNPAID_LEAVE', label: 'Unpaid Leave', color: 'default' },
//     { id: 'SICKNESS', label: 'Sickness', color: 'error' },
//     { id: 'LATENESS', label: 'Lateness', color: 'warning' },
//     { id: 'PERSONAL', label: 'Personal', color: 'secondary' }
//   ];

//   if (loading) {
//     return (
//       <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
//         <CircularProgress />
//       </Box>
//     );
//   }

//   if (error) {
//     return (
//       <Box padding={3}>
//         <Alert severity="error">{error}</Alert>
//       </Box>
//     );
//   }

//   return (
//     <Box sx={{ padding: 3 }}>
//       <Typography variant="h5" gutterBottom sx={{ marginBottom: 3 }}>
//         Attendance Report
//       </Typography>

//       <Box sx={{ marginBottom: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
//         {filterButtons.map((button) => (
//           <Button
//             key={button.id}
//             variant={activeFilter === button.id ? 'contained' : 'outlined'}
//             color={button.color}
//             onClick={() => setActiveFilter(button.id)}
//             sx={{
//               textTransform: 'none',
//               fontWeight: activeFilter === button.id ? 600 : 400
//             }}
//           >
//             {button.label}
//           </Button>
//         ))}
//       </Box>

//       <TableContainer component={Paper} elevation={2}>
//         <Table>
//           <TableHead>
//             <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
//               <TableCell sx={{ fontWeight: 600 }}>Employee Name</TableCell>
//               <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
//               <TableCell sx={{ fontWeight: 600 }}>Position</TableCell>
//               <TableCell align="center" sx={{ fontWeight: 600 }}>Days</TableCell>
//               <TableCell align="center" sx={{ fontWeight: 600 }}>Hours</TableCell>
//               <TableCell sx={{ fontWeight: 600 }}>Dates</TableCell>
//               <TableCell align="center" sx={{ fontWeight: 600 }}>Balance</TableCell>
//             </TableRow>
//           </TableHead>
//           <TableBody>
//             {renderTableContent()}
//           </TableBody>
//         </Table>
//       </TableContainer>

//       <Typography variant="caption" color="textSecondary" sx={{ marginTop: 2, display: 'block' }}>
//         Report Period: {data[0]?.reportStartDate || 'N/A'} to {data[0]?.reportEndDate || 'N/A'}
//       </Typography>
//     </Box>
//   );
// };

// export default AttendanceReportTable;